<template>
  <b-modal
    id="bulk-edit-wizard-modal"
    v-model="localIsOpen"
    :title="$t('bulk_edit.modal_title')"
    centered
    no-close-on-backdrop
    no-close-on-esc
    header-bg-variant="dark"
    header-text-variant="light"
    title-class="text-white"
    size="xl"
    @hidden="onHidden"
  >
    <!-- Step indicator (icons + arrows, mirrors the user wizard; clickable) -->
    <div class="bulk-step-indicator mb-1">
      <template v-for="(step, index) in stepTitles">
        <div
          :class="{ 'bulk-step': true, active: currentStep === index }"
          :key="'step-' + index"
          @click="goToStep(index)"
        >
          <div class="step-content">
            <feather-icon :icon="stepIcons[index]" size="25" />
            <div class="step-label text-capitalize">{{ step }}</div>
          </div>
        </div>

        <!-- Arrow after each step except the last one -->
        <div
          v-if="index < stepTitles.length - 1"
          class="arrow"
          :key="'arrow-' + index"
        >
          <feather-icon icon="ChevronRightIcon" size="30" />
        </div>
      </template>
    </div>

    <hr class="mt-0" />

    <!-- Scrollable body: keeps the title + step indicator always visible -->
    <div class="bulk-modal-body">
      <p class="text-muted small mb-2">
        {{ $t("bulk_edit.only_filled_fields") }}
      </p>

    <!-- Step 1: info (mirrors GeneralInfoStep) -->
    <div v-show="currentStep === 0" class="div-container">
      <b-row>
        <b-col cols="6">
          <!-- country -->
          <b-form-group
            class="text-capitalize"
            :label="$t('country.telephone_code')"
            label-for="bulk-id_country"
          >
            <v-select
              v-model="localFormData.id_country"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="countryOptionsUpper"
              :reduce="(val) => val.value"
              input-id="bulk-id_country"
              :placeholder="$t('bulk_edit.select_placeholder')"
              append-to-body
              :calculate-position="withPopper"
            />
          </b-form-group>
        </b-col>

        <b-col cols="6">
          <b-form-group
            class="text-capitalize"
            :label="$t('users.allowed_tabs')"
            label-for="bulk-allowed_tabs"
          >
            <b-form-input
              id="bulk-allowed_tabs"
              v-model="localFormData.allowed_tabs"
              type="number"
              min="1"
              trim
              :placeholder="DEFAULT_ALLOWED_TABS"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="6">
          <!-- vpn_access (Yes/No/no-change select) -->
          <b-form-group
            class="text-capitalize"
            :label="$t('users.vpn_access')"
            label-for="bulk-vpn_access"
          >
            <v-select
              v-model="localFormData.vpn_access"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="booleanOptions"
              :reduce="(val) => val.value"
              input-id="bulk-vpn_access"
              :placeholder="$t('bulk_edit.select_placeholder')"
              append-to-body
              :calculate-position="withPopper"
            />
          </b-form-group>

          <!-- video wall -->
          <b-form-group
            class="text-capitalize"
            label="video wall"
            label-for="bulk-video_wall"
          >
            <v-select
              v-model="localFormData.video_wall"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="booleanOptions"
              :reduce="(val) => val.value"
              input-id="bulk-video_wall"
              :placeholder="$t('bulk_edit.select_placeholder')"
              append-to-body
              :calculate-position="withPopper"
            />
          </b-form-group>

          <!-- chat -->
          <b-form-group
            class="text-capitalize"
            label="chat"
            label-for="bulk-enabled_chat"
          >
            <v-select
              v-model="localFormData.enabled_chat"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="booleanOptions"
              :reduce="(val) => val.value"
              input-id="bulk-enabled_chat"
              :placeholder="$t('bulk_edit.select_placeholder')"
              append-to-body
              :calculate-position="withPopper"
            />
          </b-form-group>

          <!-- helpdesk -->
          <b-form-group
            class="text-capitalize"
            label="Helpdesk"
            label-for="bulk-enabled_helpdesk"
          >
            <v-select
              v-model="localFormData.enabled_helpdesk"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="booleanOptions"
              :reduce="(val) => val.value"
              input-id="bulk-enabled_helpdesk"
              :placeholder="$t('bulk_edit.select_placeholder')"
              append-to-body
              :calculate-position="withPopper"
            />
          </b-form-group>

          <!-- active -->
          <b-form-group
            class="text-capitalize"
            :label="$t('users.active')"
            label-for="bulk-active"
          >
            <v-select
              v-model="localFormData.active"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="booleanOptions"
              :reduce="(val) => val.value"
              input-id="bulk-active"
              :placeholder="$t('bulk_edit.select_placeholder')"
              append-to-body
              :calculate-position="withPopper"
            />
          </b-form-group>
        </b-col>

        <b-col cols="6" v-if="$can('update_history_time', 'Alarm')">
          <!-- alarm history (mode + value, other modes reset to 0 like the wizard) -->
          <div class="d-flex align-items-center">
            <b-form-checkbox
              :checked="isAlarmHistoryActive"
              @change="toggleAlarmHistory"
              v-b-tooltip.hover
              :title="$t('bulk_edit.enable_field')"
            />
            <label class="text-capitalize mb-0">
              {{ $t("users.alarms_history") }}
            </label>
          </div>

          <b-form-radio
            v-model="alarmHistoryMode"
            name="bulk-alarm-history-mode"
            value="hours"
            class="my-1 text-capitalize"
            :disabled="!isAlarmHistoryActive"
            @input="changeAlarmHistoryMode"
          >
            {{ $t("users.alarm_history_per_hours") }}
          </b-form-radio>
          <b-form-input
            id="bulk-alarm_history_per_hours"
            v-model="localFormData.alarm_history_per_hours"
            trim
            v-show="alarmHistoryMode == 'hours'"
            type="number"
            :disabled="!isAlarmHistoryActive"
          />

          <b-form-radio
            v-model="alarmHistoryMode"
            name="bulk-alarm-history-mode"
            value="days"
            class="my-1 text-capitalize"
            :disabled="!isAlarmHistoryActive"
            @input="changeAlarmHistoryMode"
          >
            {{ $t("users.alarm_history_per_days") }}
          </b-form-radio>
          <b-form-input
            id="bulk-alarm_history_per_days"
            v-model="localFormData.alarm_history_per_days"
            trim
            v-show="alarmHistoryMode == 'days'"
            type="number"
            :disabled="!isAlarmHistoryActive"
          />

          <b-form-radio
            v-model="alarmHistoryMode"
            name="bulk-alarm-history-mode"
            value="records"
            class="my-1 text-capitalize"
            :disabled="!isAlarmHistoryActive"
            @input="changeAlarmHistoryMode"
          >
            {{ $t("users.alarm_history_by_records") }}
          </b-form-radio>
          <b-form-input
            id="bulk-alarm_history_by_records"
            v-model="localFormData.alarm_history_by_records"
            trim
            v-show="alarmHistoryMode == 'records'"
            type="number"
            :disabled="!isAlarmHistoryActive"
          />
        </b-col>
      </b-row>
    </div>

    <!-- Step 2: tenants (mirrors TenantStep) -->
    <div v-show="currentStep === 1" class="div-container">
      <!-- Master Tenant -->
      <b-form-group
        class="text-capitalize"
        :label="$tc('master_tenants.title', 2)"
        label-for="bulk-master_tenant"
      >
        <v-select
          v-model="localFormData.master_tenant"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="catalogOptions.masterTenantOptions"
          :reduce="(val) => val.value"
          input-id="bulk-master_tenant"
          multiple
          :close-on-select="false"
          :placeholder="$t('bulk_edit.select_placeholder')"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-form-group>

      <!-- Tenant -->
      <b-form-group
        class="text-capitalize"
        :label="$tc('tenants.title', 2)"
        label-for="bulk-tenant"
      >
        <v-select
          v-model="localFormData.tenant"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="catalogOptions.tenantOptions"
          :reduce="(val) => val.value"
          input-id="bulk-tenant"
          multiple
          :close-on-select="false"
          :placeholder="$t('bulk_edit.select_placeholder')"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-form-group>

      <!-- Tipos de alarma -->
      <multiple-alarms-select
        :itemData="{ type_alarm: localFormData.type_alarm ? localFormData.type_alarm : [] }"
        :options="catalogOptions.typeAlarmOptions"
        @update:itemData="localFormData.type_alarm = $event.type_alarm"
      />

      <!-- type_alarm -->
      <!--
      <b-form-group
        class="text-capitalize"
        :label="$tc('types_alarms.title', 2)"
        label-for="bulk-type_alarm"
      >
        <div class="d-flex align-items-center mb-1">
          <input
            type="checkbox"
            :checked="allTypeAlarmsSelected"
            @change="toggleAllTypeAlarms"
          />
          <span
            class="text-capitalize"
            style="margin-left: 5px; margin-bottom: -2px"
          >
            {{ $t("actions.select_deselect_all") }}
          </span>
        </div>
        <v-select
          v-model="localFormData.type_alarm"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="catalogOptions.typeAlarmOptions"
          :reduce="(val) => val.value"
          input-id="bulk-type_alarm"
          multiple
          :close-on-select="false"
          :placeholder="$t('bulk_edit.select_placeholder')"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-form-group>
      -->

      <p class="text-muted small mb-0">
        {{ $t("bulk_edit.replace_lists_hint") }}
      </p>
    </div>

    <!-- Step 3: permissions (mirrors PermissionStep) -->
    <div v-show="currentStep === 2" class="div-container">
      <b-row>
        <b-col>
          <b-form-group
            class="text-capitalize"
            :label="$tc('profiles.title', 0)"
            label-for="bulk-id_profile"
          >
            <v-select
              v-model="localFormData.id_profile"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="catalogOptions.profileOptions"
              :reduce="(val) => val.value"
              input-id="bulk-id_profile"
              :placeholder="$t('bulk_edit.select_placeholder')"
              append-to-body
              :calculate-position="withPopper"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-1">
        <b-col>
          <b-form-group
            class="text-capitalize"
            :label="$tc('app_modules.title', 2)"
            label-for="bulk-app_modules"
          >
            <b-form-checkbox-group
              id="bulk-app_modules"
              v-model="appModulesProxy"
              :options="catalogOptions.appModuleOptions"
              text-field="label"
              switches
              stacked
            ></b-form-checkbox-group>
          </b-form-group>
        </b-col>
      </b-row>

      <p class="text-muted small mb-0">
        {{ $t("bulk_edit.replace_lists_hint") }}
      </p>
    </div>

    <!-- Step 4: camera assignment (deferred selection, applied on submit) -->
    <div v-if="camerasVisited" v-show="currentStep === 3">
      <div class="camera-toolbar mb-1 px-2">
        <div class="camera-toolbar__bulk">
          <b-button
            :variant="assignAll ? 'primary' : 'primary'"
            size="sm"
            pill
            v-b-tooltip.hover
            :title="$t('bulk_edit.assign_all_warning')"
            @click="toggleAssignAll"
          >
            <feather-icon icon="CheckSquareIcon" class="mr-50" />
            {{ $t("bulk_edit.assign_all") }}
          </b-button>
          <b-button
            :variant="unassignAll ? 'secondary' : 'secondary'"
            size="sm"
            pill
            v-b-tooltip.hover
            :title="$t('bulk_edit.unassign_all_warning')"
            @click="toggleUnassignAll"
          >
            <feather-icon icon="Trash2Icon" class="mr-50" />
            {{ $t("bulk_edit.unassign_all") }}
          </b-button>
        </div>
      </div>

      <p
        v-show="!isGlobalMode && assignIds.length"
        class="camera-hint px-2 mb-1"
      >
        <b-badge
          v-if="assignIds.length"
          id="bulk-assign-count"
          variant="light-primary"
          class="cursor-pointer"
        >
          {{ $t("bulk_edit.assign_count", { count: assignIds.length }) }}
        </b-badge>
        <b-popover
          v-if="assignIds.length"
          target="bulk-assign-count"
          triggers="hover focus"
          placement="bottom"
          custom-class="camera-ids-popover"
        >
          <template #title>{{ $t("bulk_edit.assign_selected") }}</template>
          <div class="camera-ids-list">
            <div
              v-for="id in assignIds"
              :key="id"
              class="camera-ids-list__item"
            >
              <feather-icon icon="VideoIcon" size="14" class="mr-50 text-muted" />
              {{ $t("bulk_edit.device_id", { id }) }}
            </div>
          </div>
        </b-popover>
      </p>

      <b-alert variant="primary" :show="assignAll" class="mx-2">
        <div class="alert-body">{{ $t("bulk_edit.assign_all_warning") }}</div>
      </b-alert>
      <b-alert variant="warning" :show="unassignAll" class="mx-2">
        <div class="alert-body">{{ $t("bulk_edit.unassign_all_warning") }}</div>
      </b-alert>

      <!-- Kept visible in "all" modes too: its filters are what the backend
           will use to resolve which devices to assign/unassign. -->
      <device-assignment
        ref="deviceAssignmentRef"
        :config="deviceConfig"
        :assing-devices="assignedDevicesState"
        :is-parent-loading="isResolvingIds"
        @updateStatus="onDeviceUpdateStatus"
      />
    </div>
    </div>
    <!-- /bulk-modal-body -->

    <!-- Footer -->
    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$emit('close')"
        :disabled="isSubmitting"
        class="text-capitalize mr-auto"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        v-if="currentStep > 0"
        variant="outline-primary"
        @click="goToStep(currentStep - 1)"
        :disabled="isSubmitting"
        class="text-capitalize"
      >
        {{ $t("actions.previous") }}
      </b-button>
      <b-button
        v-if="currentStep < stepTitles.length - 1"
        variant="outline-primary"
        @click="goToStep(currentStep + 1)"
        :disabled="isSubmitting"
        class="text-capitalize"
      >
        {{ $t("actions.next") }}
      </b-button>
      <b-button
        variant="primary"
        @click="onApply"
        :disabled="isSubmitting || !hasChanges"
        class="text-capitalize"
      >
        <b-spinner small v-if="isSubmitting" class="mr-50" />
        <span>
          {{ $t("bulk_edit.apply_changes", { count: selectedCount }) }}
        </span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormRadio,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import store from "@/store";
import popperHelper from "@/libs/popper-helper";
import DeviceAssignment from "@/components/DeviceAssignment/Index.vue";
import MultipleAlarmsSelect from "@/components/Alarm/MultipleAlarmsSelect";

const DEFAULT_ALLOWED_TABS = 5;

export default {
  name: "BulkEditWizardModal",
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormCheckboxGroup,
    BFormRadio,
    vSelect,
    DeviceAssignment,
    MultipleAlarmsSelect,
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    selectedCount: {
      type: Number,
      default: 0,
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    catalogOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit, parent }) {
    const currentStep = ref(0);
    // Mount DeviceAssignment lazily (first visit) and keep it alive with
    // v-show afterwards so its local filters survive step switches.
    const camerasVisited = ref(false);
    const isResolvingIds = ref(false);

    // Same steps and titles as the user wizard.
    const stepTitles = computed(() => [
      parent.$i18n.t("users.info"),
      parent.$i18n.tc("tenants.title", 2),
      parent.$i18n.tc("permissions.title", 2),
      parent.$i18n.tc("camera.title", 2),
    ]);

    // Same icons as WizardStepIndicator (info / tenants / permissions / cameras).
    const stepIcons = ["UserIcon", "UsersIcon", "CheckCircleIcon", "VideoIcon"];

    const localIsOpen = computed({
      get() {
        return props.isOpen;
      },
      set(value) {
        if (!value) {
          emit("close");
        }
      },
    });

    // Reactive proxy of formData for two-way binding on the fields.
    const localFormData = computed(() => props.formData);

    const capitalize = (s) =>
      typeof s === "string" && s.length
        ? s.charAt(0).toUpperCase() + s.slice(1)
        : s;

    // --- Tri-state booleans ---
    // A clearable Yes/No select: null (cleared) = untouched, otherwise the
    // value travels as "1"/"0" like the wizard switches.
    const booleanOptions = computed(() => [
      { label: capitalize(parent.$i18n.t("yes")), value: "1" },
      { label: capitalize(parent.$i18n.t("no")), value: "0" },
    ]);

    // Phone-code options in uppercase (e.g. "USA (+1)").
    const countryOptionsUpper = computed(() =>
      (props.catalogOptions.countryOptions || []).map((option) => ({
        ...option,
        label: (option.label || "").toUpperCase(),
      }))
    );

    // A field is "set" when it is not null/undefined (used by the alarm
    // history block below to know whether it should be sent).
    const isFieldActive = (key) =>
      localFormData.value[key] !== null &&
      localFormData.value[key] !== undefined;

    // --- Alarm history (exclusive mode + value, mirrors GeneralInfoStep) ---
    const alarmHistoryMode = ref("hours");
    const ALARM_HISTORY_KEYS = [
      "alarm_history_per_hours",
      "alarm_history_per_days",
      "alarm_history_by_records",
    ];

    const isAlarmHistoryActive = computed(() =>
      ALARM_HISTORY_KEYS.some((key) => isFieldActive(key))
    );

    const toggleAlarmHistory = (enabled) => {
      alarmHistoryMode.value = "hours";
      ALARM_HISTORY_KEYS.forEach((key) => {
        localFormData.value[key] = enabled ? 0 : null;
      });
    };

    // Switching mode resets the three values to 0, like the wizard does.
    const changeAlarmHistoryMode = () => {
      ALARM_HISTORY_KEYS.forEach((key) => {
        localFormData.value[key] = 0;
      });
    };

    // --- type_alarm select/deselect all (mirrors TenantStep) ---
    const allTypeAlarmsSelected = computed(() => {
      const options = props.catalogOptions.typeAlarmOptions || [];
      const selected = localFormData.value.type_alarm || [];
      return options.length > 0 && selected.length >= options.length;
    });

    const toggleAllTypeAlarms = () => {
      if (allTypeAlarmsSelected.value) {
        localFormData.value.type_alarm = [];
        return;
      }
      localFormData.value.type_alarm = (
        props.catalogOptions.typeAlarmOptions || []
      ).map((item) => item.value);
    };

    // --- app_modules checkbox group (mirrors PermissionStep) ---
    // The group needs an array; null (untouched) is exposed as [].
    const appModulesProxy = computed({
      get() {
        return localFormData.value.app_modules || [];
      },
      set(value) {
        localFormData.value.app_modules = value;
      },
    });

    // --- Deferred camera selection (applied on submit) ---
    // The table's checkbox selection feeds `assignIds` (devices to assign).
    // Global modes below cover "assign all" / "unassign all".
    const assignIds = ref([]);
    const assignAll = ref(false);
    const unassignAll = ref(false);

    // Object handed to DeviceAssignment. It paints its checkboxes from
    // `cameraProxyIds`: mutating in place keeps the selection across pagination
    // without a refetch.
    const assignedDevicesState = ref({ cameraProxyIds: [], count: 0 });

    // Template ref to read the device table's active filters (they are part of
    // its setup() return), used by the assign_all / unassign_all payload.
    const deviceAssignmentRef = ref(null);

    // Active camera filters, with the same keys the device list queries with
    // (see DeviceAssignment/useList fetchList), so the backend resolves the
    // same set. Empty values are omitted.
    const buildCameraFilters = () => {
      const table = deviceAssignmentRef.value;
      if (!table) return {};

      const candidates = {
        q: table.searchQuery,
        units: table.unitsFilter,
        id_sector: table.sectorFilter,
        id_center: table.centerFilter,
        online: table.onlineFilter,
        tenant: table.tenantFilter,
      };

      const active = {};
      Object.entries(candidates).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          active[key] = value;
        }
      });
      return active;
    };

    // No id_user: the list shows every device; selection applies on submit.
    const deviceConfig = {
      filters: {
        tenant: true,
        center: true,
        sector: true,
        units: true,
        online: true,
        assing: false,
      },
    };

    const isGlobalMode = computed(() => assignAll.value || unassignAll.value);
    const selectedCamerasCount = computed(
      () => assignedDevicesState.value.cameraProxyIds.length
    );

    const applyIdsToSelection = (ids, status) => {
      const current = assignedDevicesState.value.cameraProxyIds;
      const selected = new Set(current);
      const add = String(status) === "1";
      (ids || []).forEach((id) => (add ? selected.add(id) : selected.delete(id)));
      current.splice(0, current.length, ...selected);
      assignedDevicesState.value.count = current.length;
      // Mirror the visible selection into the assign list.
      assignIds.value = [...current];
    };

    const onDeviceUpdateStatus = async (data) => {
      // Picking devices by hand cancels the "all" modes.
      if (isGlobalMode.value) {
        assignAll.value = false;
        unassignAll.value = false;
      }

      if (!data.multiple) {
        applyIdsToSelection([data.id], data.status);
        return;
      }

      if (data.ids) {
        applyIdsToSelection(data.ids, data.status);
        return;
      }

      // Header check-all without explicit ids: resolve the matching ids with
      // the active filters (same query the user wizard runs on updateStatus).
      isResolvingIds.value = true;
      try {
        const response = await store.dispatch("camera_proxy/getAll", {
          q: data.searchQuery,
          units: data.id_unit,
          id_sector: data.id_sector,
          id_center: data.id_center,
          online: data.online,
          tenant: data.id_tenant,
          action: "justIds",
        });
        applyIdsToSelection(response.data.cameraProxyIds, data.status);
      } finally {
        isResolvingIds.value = false;
      }
    };

    // Global modes clear the granular selection and hide the device table.
    // The two are mutually exclusive with each other.
    const clearGranularSelection = () => {
      assignIds.value = [];
      assignedDevicesState.value = { cameraProxyIds: [], count: 0 };
    };

    const toggleAssignAll = () => {
      assignAll.value = !assignAll.value;
      if (assignAll.value) {
        unassignAll.value = false;
        clearGranularSelection();
      }
    };

    const toggleUnassignAll = () => {
      unassignAll.value = !unassignAll.value;
      if (unassignAll.value) {
        assignAll.value = false;
        clearGranularSelection();
      }
    };

    const goToStep = (step) => {
      if (step === 3) {
        camerasVisited.value = true;
      }
      currentStep.value = step;
    };

    // Empty strings and empty arrays count as untouched (see useBulkEdit).
    const hasChanges = computed(() => {
      const fieldChanged = Object.values(props.formData).some(
        (v) =>
          v !== null &&
          v !== undefined &&
          v !== "" &&
          !(Array.isArray(v) && v.length === 0)
      );
      const camerasTouched =
        assignAll.value ||
        unassignAll.value ||
        assignedDevicesState.value.cameraProxyIds.length > 0;
      return fieldChanged || camerasTouched;
    });

    // Cameras payload — mutually exclusive modes:
    //   { assign: [ids] }                      assign the picked devices
    //   { assign_all: true, filters: {...} }   assign every device matching
    //   { unassign_all: true, filters: {...} } remove every device matching
    // The *_all modes send the table filters so the backend runs the search.
    const onApply = () => {
      // Capture the visible selection first.
      assignIds.value = [...assignedDevicesState.value.cameraProxyIds];

      const cameras = {};
      if (assignAll.value) {
        cameras.assign_all = true;
        cameras.filters = buildCameraFilters();
      } else if (unassignAll.value) {
        cameras.unassign_all = true;
        cameras.filters = buildCameraFilters();
      } else if (assignIds.value.length) {
        cameras.assign = [...assignIds.value];
      }

      emit("submit", Object.keys(cameras).length ? { cameras } : undefined);
    };

    const onHidden = () => {
      currentStep.value = 0;
      camerasVisited.value = false;
      assignAll.value = false;
      unassignAll.value = false;
      assignIds.value = [];
      assignedDevicesState.value = { cameraProxyIds: [], count: 0 };
      alarmHistoryMode.value = "hours";
      emit("close");
    };

    return {
      currentStep,
      stepTitles,
      stepIcons,
      camerasVisited,
      isResolvingIds,
      assignAll,
      unassignAll,
      isGlobalMode,
      assignIds,
      localIsOpen,
      localFormData,
      booleanOptions,
      countryOptionsUpper,
      alarmHistoryMode,
      isAlarmHistoryActive,
      toggleAlarmHistory,
      changeAlarmHistoryMode,
      allTypeAlarmsSelected,
      toggleAllTypeAlarms,
      appModulesProxy,
      assignedDevicesState,
      deviceAssignmentRef,
      deviceConfig,
      selectedCamerasCount,
      onDeviceUpdateStatus,
      toggleAssignAll,
      toggleUnassignAll,
      goToStep,
      hasChanges,
      onApply,
      onHidden,
      withPopper: popperHelper.withPopper,
      DEFAULT_ALLOWED_TABS,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/variables/_variables";

.modal-header .bg-dark {
  background-color: var(--main-dark) !important;
}

.div-container {
  max-width: 80%;
  margin: auto;
}

// Fixed-height scroll area so the modal title + step indicator stay visible
// (the cameras step can be taller than the viewport).
.bulk-modal-body {
  height: 60vh;
  overflow-y: auto;
}

// Cameras step action bar
.camera-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  text-transform: none;
}

.camera-toolbar__bulk {
  display: flex;
  gap: 0.5rem;
}

.camera-hint {
  text-transform: none;
}

// Step indicator (mirrors WizardStepIndicator)
.bulk-step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.bulk-step {
  display: flex;
  align-items: center;
  margin-right: 10px;
  cursor: pointer;

  .step-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-right: 10px;
    opacity: 0.6;
    transition: opacity 0.2s ease, color 0.2s ease;
  }

  &.active {
    color: $secondary;

    .step-content {
      opacity: 1;
    }
  }
}

.step-label {
  margin-top: 5px;
}

.arrow {
  display: flex;
  align-items: center;
  margin-right: 10px;
  color: #b9b9c3;
}
</style>

<!-- Popover content renders outside the component (teleported), so its
     styles must be global, scoped by the custom-class instead. -->
<style lang="scss">
.camera-ids-popover {
  max-width: 260px;

  .popover-body {
    padding: 0.35rem 0.75rem;
  }

  .camera-ids-list {
    max-height: 220px;
    overflow-y: auto;
  }

  .camera-ids-list__item {
    display: flex;
    align-items: center;
    padding: 0.2rem 0;
    font-size: 0.85rem;

    &:not(:last-child) {
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }
  }
}
</style>
